import { useState, useEffect, useCallback, useRef } from 'react';
import { guestAPI, GuestPost } from '../utils/guestApi';

interface UseGuestPostsParams {
  type?: string;
  searchQuery?: string;
  tag?: string;
  limit?: number;
}

interface GuestPostsResponse {
  posts: GuestPost[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

export const useGuestPosts = (params: UseGuestPostsParams = {}, enabled: boolean = true): {
  posts: GuestPost[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  fetchPosts: (refresh?: boolean) => Promise<void>;
  loadMore: () => void;
  retry: () => void;
} => {
  const [posts, setPosts] = useState<GuestPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pageRef = useRef(1);
  const [hasMore, setHasMore] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: params.limit || 10,
    total: 0,
  });
  // usePosts와 같은 규칙: 진행 중 여부·요청 세대는 ref로 관리해 중복 요청과 늦은 응답 섞임을 막는다
  const loadingRef = useRef(false);
  const requestIdRef = useRef(0);

  const fetchPosts = useCallback(async (refresh = false) => {
    if (!refresh && loadingRef.current) return;

    const requestId = ++requestIdRef.current;
    loadingRef.current = true;
    setLoading(true);
    setError(null);

    const currentPage = refresh ? 1 : pageRef.current;

    try {
      const response = await guestAPI.getPosts({
        type: params.type,
        q: params.searchQuery,
        tag: params.tag,
        page: currentPage,
        limit: pagination.limit,
      } as any);
      if (requestId !== requestIdRef.current) return;
      const data: GuestPostsResponse = response.data;

      setPosts(prevPosts => {
        if (refresh) return data.posts;
        const existingIds = new Set(prevPosts.map(p => p.id));
        return [...prevPosts, ...data.posts.filter(p => !existingIds.has(p.id))];
      });
      setPagination(data.pagination);
      // total은 문자열로 올 수 있다(PG COUNT) — 숫자로 비교하고, 빈 페이지면 끝으로 본다
      setHasMore(
        data.posts.length > 0 &&
        Number(data.pagination.page) * Number(data.pagination.limit) < Number(data.pagination.total)
      );
      pageRef.current = currentPage + 1;
    } catch (err: any) {
      if (requestId !== requestIdRef.current) return;
      setError(err.response?.data?.error || '게시글을 불러오는데 실패했습니다.');
    } finally {
      if (requestId === requestIdRef.current) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  }, [params.type, params.searchQuery, params.tag, pagination.limit]);

  useEffect(() => {
    if (enabled) {
      pageRef.current = 1;
      setPosts([]);
      setHasMore(true);
      fetchPosts(true);
    } else {
      requestIdRef.current++;
      loadingRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.type, params.searchQuery, params.tag, enabled]);

  const loadMore = () => {
    if (!loadingRef.current && hasMore && !error) {
      fetchPosts();
    }
  };

  const retry = () => {
    fetchPosts(posts.length === 0);
  };

  return {
    posts,
    loading,
    error,
    hasMore,
    pagination,
    fetchPosts,
    loadMore,
    retry,
  };
};
